import React, { useMemo } from "react";
import { Flame, Trophy, Percent, Target, Star, Gamepad2 } from "lucide-react";
import { computeStats, getDailyHistory } from "../lib/storage";
import { GuessTiles } from "../components/GuessTiles";
import { prettyDate } from "../lib/ist";

const Big = ({ label, value, icon: Icon, accent }) => (
  <div className="ac-card p-4 sm:p-5 text-center" data-testid={`stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>
    <Icon className={`w-5 h-5 mx-auto mb-2 ${accent || "ac-gold-text"}`} />
    <div className="text-2xl sm:text-3xl font-black text-zinc-50">{value}</div>
    <div className="text-[10px] uppercase tracking-widest text-zinc-500 mt-1">{label}</div>
  </div>
);

export const Stats = () => {
  const stats = useMemo(() => computeStats(), []);
  const history = useMemo(() => getDailyHistory(), []);

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 py-8 space-y-6" data-testid="stats-page">
      <div className="text-center">
        <h1 className="font-display text-3xl sm:text-4xl font-black tracking-wider ac-gold-text">
          📊 YOUR STATS
        </h1>
        <p className="text-zinc-500 text-sm mt-1">Daily Challenge performance</p>
      </div>

      <div className="grid grid-cols-3 gap-3">
        <Big label="Games Played" value={stats.played} icon={Gamepad2} />
        <Big label="Wins" value={stats.wins} icon={Trophy} />
        <Big label="Win Rate" value={`${stats.winRate}%`} icon={Percent} />
      </div>
      <div className="grid grid-cols-3 gap-3">
        <Big label="Current Streak" value={`${stats.currentStreak} 🔥`} icon={Flame} accent="text-orange-400" />
        <Big label="Longest Streak" value={stats.longestStreak} icon={Flame} accent="text-amber-300" />
        <Big label="Avg Guesses" value={stats.avgGuesses} icon={Target} />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Big label="Total Score" value={stats.totalScore.toLocaleString()} icon={Star} />
        <Big label="Average Score" value={stats.avgScore} icon={Star} />
      </div>

      {/* history */}
      <div>
        <h2 className="font-display text-xl font-bold text-amber-200 mb-3">Daily History</h2>
        {history.length === 0 ? (
          <div className="ac-card p-8 text-center text-zinc-500 text-sm" data-testid="history-empty">
            No games played yet. Solve today's movie to start your streak!
          </div>
        ) : (
          <div className="space-y-2" data-testid="daily-history">
            {history.map((g) => (
              <div
                key={g.date}
                data-testid={`history-row-${g.date}`}
                className="ac-card p-3 sm:p-4 flex items-center justify-between gap-3"
              >
                <div>
                  <div className="font-semibold text-zinc-100 text-sm">{prettyDate(g.date)}</div>
                  <div className={`text-xs ${g.status === "won" ? "text-emerald-400" : "text-rose-400"}`}>
                    {g.status === "won"
                      ? `Won · ${g.guessesUsed} guess${g.guessesUsed === 1 ? "" : "es"} · ${g.score} pts`
                      : `Lost · 5 guesses · 0 pts`}
                  </div>
                </div>
                <GuessTiles tiles={g.tiles} size="sm" />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
